/**
 * Plan of Record baseline for Scenario Comparison — read-only.
 *
 * The POR is never picked: it is derived from the active scenario's cycle
 * binding (one current POR per cycle; `plan_of_record_snapshots` WHERE
 * superseded_by_snapshot_id IS NULL). Unbound scenarios and cycles without a
 * POR simply have no baseline — absence never blocks a run (Doc 102 §2A).
 *
 * Matching: live-item-synced engine priorities carry the item id as their
 * priority id (itemToPlanningAdapter), and POR initiatives carry `item_id` —
 * so id equality is the primary key; case-insensitive title match is the
 * fallback for manually created scenario priorities.
 */

import { getPlanCycleById, getCurrentPorSnapshot } from './planningService';
import type { PrioritySummary } from './planningEngineModule.types';

export interface PorBaselineInitiative {
  itemId: string;
  title: string;
  position: number | null;
}

export interface PorBaseline {
  cycleId: string;
  cycleName: string;
  authorizedAt: string;
  /** Authorized (non-dropped) initiatives, in priority_position order. */
  initiatives: PorBaselineInitiative[];
}

export interface PorDeltas {
  /** POR initiatives with no matching priority in the run at all. */
  notInRun: PorBaselineInitiative[];
  /** POR initiatives present in the run but Not Achievable under it. */
  atRisk: PorBaselineInitiative[];
  /** Run priorities (at least partially achievable) that are not in the POR. */
  beyondPor: { id: string; name: string }[];
}

function titleKey(s: string): string {
  return s.trim().toLowerCase();
}

/** Load the derived POR baseline for a cycle, or null when the cycle has no current POR. */
export async function loadPorBaseline(planCycleId: string): Promise<PorBaseline | null> {
  const [{ data: snapshot }, { data: cycle }] = await Promise.all([
    getCurrentPorSnapshot(planCycleId),
    getPlanCycleById(planCycleId),
  ]);
  if (!snapshot) return null;

  const initiatives = (snapshot.payload?.initiatives ?? [])
    .filter((i) => i.status !== 'dropped')
    .map((i) => ({
      itemId: i.item_id,
      title: i.title?.trim() || i.item_id,
      position: i.priority_position,
    }))
    .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER));

  return {
    cycleId: planCycleId,
    cycleName: cycle?.name ?? 'Plan cycle',
    authorizedAt: snapshot.authorized_at,
    initiatives,
  };
}

/** Is this run priority part of the authorized POR? Id match first, title fallback. */
export function isInPor(
  baseline: PorBaseline,
  priorityId: string,
  priorityName: string,
): boolean {
  return baseline.initiatives.some(
    (i) => i.itemId === priorityId || titleKey(i.title) === titleKey(priorityName),
  );
}

/** Deltas between the authorized POR and one scenario run's priority summaries. */
export function computePorDeltas(
  baseline: PorBaseline,
  summaries: PrioritySummary[],
): PorDeltas {
  const byId = new Map(summaries.map((s) => [s.priority.id, s]));
  const byTitle = new Map(summaries.map((s) => [titleKey(s.priority.name), s]));

  const notInRun: PorBaselineInitiative[] = [];
  const atRisk: PorBaselineInitiative[] = [];
  for (const init of baseline.initiatives) {
    const match = byId.get(init.itemId) ?? byTitle.get(titleKey(init.title));
    if (!match) notInRun.push(init);
    else if (match.feasibility === 'Not Achievable') atRisk.push(init);
  }

  const beyondPor = summaries
    .filter(
      (s) =>
        (s.feasibility === 'Fully Achievable' || s.feasibility === 'Partially Achievable') &&
        !isInPor(baseline, s.priority.id, s.priority.name),
    )
    .map((s) => ({ id: s.priority.id, name: s.priority.name }));

  return { notInRun, atRisk, beyondPor };
}
